import React from 'react';

import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import { getRouteEditUser } from '@src/packages/users/constants';
import { getFullUrl } from '@src/packages/utils/functions';

export type MemberLike = {
  id?: string;
  user?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
  } | null;
};

export type AssignedMemberIssueExtras = {
  userIds?: string[];
  displayNames?: string[];
};

export const getMemberUserDisplayName = (user?: MemberLike['user'] | null): string => {
  if (!user) {
    return '';
  }
  const fullName = [user.firstName, user.lastName]
    .filter(part => typeof part === 'string' && part.trim())
    .join(' ')
    .trim();
  if (fullName) {
    return fullName;
  }
  if (typeof user.displayName === 'string' && user.displayName.trim()) {
    return user.displayName.trim();
  }
  if (typeof user.email === 'string' && user.email.trim()) {
    return user.email.trim();
  }
  return typeof user.id === 'string' ? user.id : '';
};

export const collectAssignedMemberIssueUsers = (
  extras: AssignedMemberIssueExtras,
  members: MemberLike[] = [],
): Array<{ id: string; name: string }> => {
  const membersById = new Map<string, MemberLike['user']>();
  members.forEach(member => {
    const id = member.user?.id || member.id;
    if (id) {
      membersById.set(id, member.user ?? null);
    }
  });

  const userIds = Array.isArray(extras.userIds)
    ? extras.userIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  const displayNames = Array.isArray(extras.displayNames)
    ? extras.displayNames.filter(
        (name): name is string => typeof name === 'string' && name.trim().length > 0,
      )
    : [];

  if (userIds.length > 0) {
    return userIds.map((id, index) => {
      const fromMember = getMemberUserDisplayName(membersById.get(id));
      const fromError = displayNames[index];
      return { id, name: fromMember || fromError || id };
    });
  }

  return displayNames.map((name, index) => ({
    id: `name-${index}`,
    name,
  }));
};

/**
 * Shared toast body: intro copy + linked user list (opens edit profile in a new tab).
 */
export const buildAssignedMembersIssueDescription = (
  intl: IntlShape,
  messageId: string,
  users: Array<{ id: string; name: string }>,
  options: {
    matchUrl?: string;
    messageValues?: Record<string, string | number>;
  } = {},
): ReactNode => {
  const count = Math.max(users.length, 1);
  const intro = intl.formatMessage(
    { id: messageId },
    { count, ...(options.messageValues || {}) },
  );

  if (users.length === 0) {
    return intro;
  }

  const matchUrl =
    options.matchUrl || (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const canLink = Boolean(matchUrl);

  return (
    <div>
      <div>{intro}</div>
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {users.map(user => {
          if (!canLink || user.id.startsWith('name-') || !matchUrl) {
            return <li key={user.id}>{user.name}</li>;
          }

          const editPath = getFullUrl(getRouteEditUser(user.id), matchUrl);
          return (
            <li key={user.id}>
              <a href={editPath} target="_blank" rel="noopener noreferrer">
                {user.name}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
